import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import React, { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { Card } from '../Card';
import { cn } from '../lib/utils';

type Orientation = 'vertical' | 'horizontal';
type Color = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';

// ── Dot colors ────────────────────────────────────────────────────────────────

const timelineDotColors: Record<Color, string> = {
  neutral: 'bg-uniform-gray-500 dark:bg-uniform-gray-400',
  primary: 'bg-green-600 dark:bg-green-500',
  secondary: 'bg-blue-600 dark:bg-blue-500',
  destructive: 'bg-uniform-red-500 dark:bg-uniform-red-400',
  warning: 'bg-yellow-500 dark:bg-yellow-400'
};

// ── Context ───────────────────────────────────────────────────────────────────

interface TimelineCtxValue {
  orientation: Orientation;
  total: number;
}

const TimelineCtx = createContext<TimelineCtxValue>({ orientation: 'vertical', total: 0 });

// ── Variants ──────────────────────────────────────────────────────────────────

const timelineVariants = tv({
  slots: {
    root: '',
    item: '',
    dotCol: '',
    // Font size controls the FA icon size (1em). Color classes handled separately.
    dot: [
      'relative z-10 flex shrink-0 items-center justify-center rounded-full text-white',
      'ring-2 ring-white dark:ring-uniform-gray-900'
    ],
    connector: 'shrink-0 bg-uniform-gray-200 dark:bg-uniform-gray-700',
    lineHalf: 'bg-uniform-gray-200 dark:bg-uniform-gray-700',
    body: '',
    date: 'text-xs font-medium text-uniform-gray-600 dark:text-uniform-gray-400',
    content: 'text-sm text-uniform-gray-700 dark:text-uniform-gray-300',
    title: 'text-sm font-medium text-uniform-gray-900 dark:text-uniform-gray-100',
    chevron: 'h-3 w-3 shrink-0 text-uniform-gray-500 transition-transform duration-200'
  },
  variants: {
    orientation: {
      vertical: {
        root: 'flex flex-col',
        item: 'flex gap-4',
        dotCol: 'flex flex-col items-center',
        dot: 'h-4 w-4 text-[8px]',
        connector: 'mt-2 flex-1 w-0.5',
        body: 'flex-1 min-w-0 pt-0.5 pb-12',
        date: 'mb-2'
      },
      horizontal: {
        root: 'flex overflow-x-auto pb-2',
        item: 'flex flex-col items-center min-w-40 flex-1',
        dotCol: 'flex w-full items-center',
        dot: 'h-4 w-4 shrink-0 text-[7px]',
        lineHalf: 'flex-1 h-0.5',
        body: 'w-full px-3 pt-3 text-center',
        date: 'mb-1',
        content: 'text-center',
        title: 'text-center'
      }
    }
  },
  defaultVariants: { orientation: 'vertical' }
});

type TimelineVariants = VariantProps<typeof timelineVariants>;

// ── Timeline (root) ───────────────────────────────────────────────────────────

interface TimelineProps {
  orientation?: Orientation;
  children?: ReactNode;
  className?: string;
}

function Timeline({ orientation = 'vertical', children, className }: TimelineProps) {
  const { root } = timelineVariants({ orientation });
  const items = React.Children.toArray(children);
  return (
    <TimelineCtx.Provider value={{ orientation, total: items.length }}>
      <div className={cn(root(), className)}>
        {items.map((child, index) =>
          React.isValidElement(child)
            ? React.cloneElement(child as React.ReactElement<{ _index: number }>, { _index: index })
            : child
        )}
      </div>
    </TimelineCtx.Provider>
  );
}

// ── TimelineItem ──────────────────────────────────────────────────────────────

interface TimelineItemProps {
  date?: string;
  icon?: IconDefinition;
  color?: Color;
  /**
   * When provided, the item body is rendered as a collapsible accordion.
   * `children` become the expandable panel content.
   */
  title?: string;
  /** Initial open state when `title` is set. Defaults to false. */
  defaultOpen?: boolean;
  /** When true, scrolls this item into view on mount. */
  autoScroll?: boolean;
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
  /** @internal injected by Timeline */
  _index?: number;
}

function TimelineItem({
  date,
  icon,
  color = 'neutral',
  title,
  defaultOpen = false,
  autoScroll = false,
  children,
  className,
  'data-testid': dataTestId,
  _index = 0
}: TimelineItemProps) {
  const { orientation, total } = useContext(TimelineCtx);
  const vars = timelineVariants({ orientation });
  const dotColor = timelineDotColors[color];
  const isFirst = _index === 0;
  const isLast = _index === total - 1;

  const itemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (autoScroll && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [autoScroll]);

  const dotEl = (
    <div className={cn(vars.dot(), dotColor)}>{icon && <FontAwesomeIcon icon={icon} />}</div>
  );

  const bodyContent = title ? (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <>
          <DisclosureButton className="flex w-full items-start justify-between gap-2 [outline:none] focus-visible:ring-1 focus-visible:ring-uniform-gray-400 rounded">
            <div className="flex-1 min-w-0 text-left">
              {date && <div className={vars.date()}>{date}</div>}
              <div className={vars.title()}>{title}</div>
            </div>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={cn(vars.chevron(), 'mt-0.5', open && 'rotate-180')}
            />
          </DisclosureButton>
          <DisclosurePanel>
            <Card padding="sm" className="mt-2 mb-4">
              <div className={vars.content()}>{children}</div>
            </Card>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  ) : (
    <>
      {date && <div className={vars.date()}>{date}</div>}
      <div className={vars.content()}>{children}</div>
    </>
  );

  if (orientation === 'horizontal') {
    return (
      <div ref={itemRef} data-testid={dataTestId} className={cn(vars.item(), className)}>
        <div className={vars.dotCol()}>
          <div className={cn(vars.lineHalf(), isFirst && 'invisible')} />
          {dotEl}
          <div className={cn(vars.lineHalf(), isLast && 'invisible')} />
        </div>
        <div className={vars.body()}>{bodyContent}</div>
      </div>
    );
  }

  return (
    <div ref={itemRef} data-testid={dataTestId} className={cn(vars.item(), className)}>
      <div className={vars.dotCol()}>
        {dotEl}
        {!isLast && <div className={vars.connector()} />}
      </div>
      <div className={cn(vars.body(), isLast && 'pb-0')}>{bodyContent}</div>
    </div>
  );
}

export { Timeline, TimelineItem, timelineVariants };
export type { TimelineProps, TimelineItemProps, TimelineVariants };
